import NumberFlow from "@number-flow/react";
import { MapPin, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  UseFieldArrayRemove,
  UseFormReturn,
  useFieldArray,
  useFormContext,
} from "react-hook-form";

import LocationMap, { MarkerData } from "./location-map";
import { MapAddress } from "./map-location.interface";

import FormInput from "@/components/form-fields/form-input";
import { FileUploaderComponent } from "@/components/reusables/FileUpload";
import { FormSelectSingle } from "@/components/reusables/custom-form-fields/FormSelect";
import { TimePicker12 } from "@/components/reusables/time-picker/time-picker-12hour";
import { Period } from "@/components/reusables/time-picker/time-picker-utils";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import * as Table from "@/components/ui/table";
import { allowedImageTypes, dayNames } from "@/constants/constants";
import { IEmployee } from "@/interfaces/employee.interface";
import { IDays } from "@/interfaces/interfaces";
import { Holiday, TimeShift } from "@/interfaces/timeTracking";
import { cn } from "@/lib/utils";
import {
  useGetShiftsQuery,
  // useGetTimeSettingDataQuery,
} from "@/redux/api/timeTrackingSettingQuery";
import { useGetUsersByWSQuery } from "@/redux/api/userQuery";
import { TDailyReport } from "@/schemas/daily-report.schema";
import {
  // calculateAllowedBreak,
  calculateTotalUserHours,
  dateToSec,
  getMainBreakDown,
  secToDate,
  secToDateFormat,
  sortShifts,
} from "@/utils/helper-functions/date-time.helper";
import { InputKeyLimiter } from "@/utils/helper-functions/form.helper";

export const SingleEmployeeWH = ({
  index,
  field,
  remove,
  employees,
  isLoading,
  form,
  shifts,
  projectAddress,
  holiday,
}: {
  index: number;
  field: Record<"arrayId", string>;
  remove: UseFieldArrayRemove;
  employees: IEmployee[];
  isLoading: boolean;
  form: UseFormReturn<TDailyReport>;
  shifts: TimeShift[];
  projectAddress?: MapAddress;
  holiday: Holiday | undefined;

  // breakList: IBreak[];
}): React.JSX.Element => {
  const startDate = secToDate(
    +form.getValues(`employeeWorkingHours.${index}.startTime`),
  );
  const endDate = secToDate(
    +form.getValues(`employeeWorkingHours.${index}.endTime`),
  );

  const [startTime, setStartTime] = useState<Date | undefined>(
    new Date(startDate ?? new Date().setHours(0, 0, 0, 0)),
  );
  const [endTime, setEndTime] = useState<Date | undefined>(
    new Date(endDate ?? new Date().setHours(0, 0, 0, 0)),
  );

  const [openLocationMap, setOpenLocationMap] = useState(false);

  const [startPeriod, setStartPeriod] = useState<Period>(
    dateToSec(startTime) > 43199 ? "PM" : "AM",
  );
  const [endPeriod, setEndPeriod] = useState<Period>(
    dateToSec(endTime) > 43199 ? "PM" : "AM",
  );

  const dayDate = new Date(form?.getValues("date") || "");
  const day = dayNames[dayDate.getDay()] as IDays;

  const handleTime = (date: Date): number => {
    if (startTime === endTime) {
      const tempDate = new Date(new Date().setHours(0, 0, 0, 0));

      return dateToSec(tempDate);
    }

    return dateToSec(date);
  };

  const breakDowns = getMainBreakDown({
    startTime: dateToSec(startTime),
    endTime: dateToSec(endTime),
    shifts,
    day,
    holiday,
  });

  const handleWhDelete = (index: number, id: string | undefined): void => {
    if (typeof id === "undefined") {
      remove(index);

      return;
    }
    const currentDeletedList =
      form.getValues("deleteEmployeeWorkingHours") || [];
    const newDeletedList = [...currentDeletedList, id];

    form.setValue(`deleteEmployeeWorkingHours`, newDeletedList, {
      shouldDirty: true,
    });
    remove(index);
  };

  useEffect(() => {
    const NH =
      +breakDowns
        ?.filter((b) => b.title === "Normal Hours")
        .reduce((acc, b) => acc + b.time, 0) || 0;

    const ON =
      +breakDowns
        ?.filter((b) => b.title === "Overnight Hours")
        .reduce((acc, b) => acc + b.time, 0) || 0;

    const WH =
      +breakDowns
        ?.filter((b) => b.title === "Weekend")
        .reduce((acc, b) => acc + b.time, 0) || 0;

    const TH = +calculateTotalUserHours(startTime!, endTime!).toFixed(2);

    const NHH = form.getValues(
      `employeeWorkingHours.${index}.workBreakDown.${0}.time`,
    );
    const ONN = form.getValues(
      `employeeWorkingHours.${index}.workBreakDown.${1}.time`,
    );
    const WHH = form.getValues(
      `employeeWorkingHours.${index}.workBreakDown.${2}.time`,
    );

    if (day && (NH !== NHH || ONN !== ON || WH !== WHH)) {
      form.setValue(
        `employeeWorkingHours.${index}.workBreakDown.${0}.time`,
        NH,
        { shouldDirty: true },
      );
      form.setValue(
        `employeeWorkingHours.${index}.workBreakDown.${1}.time`,
        ON,
        { shouldDirty: true },
      );
      form.setValue(
        `employeeWorkingHours.${index}.workBreakDown.${2}.time`,
        WH,
      );
      form.setValue(`employeeWorkingHours.${index}.totalHours`, TH);
    }
  }, [
    startTime,
    endTime,
    day,
    startPeriod,
    endPeriod,
    startDate,
    endDate,
    breakDowns,
    form,
    index,
  ]);

  const commonCellClassName = "bg-background hover:bg-background";

  return (
    <Table.TableRow key={field.arrayId}>
      <Table.TableCell width={10} className={cn(commonCellClassName)}>
        {index + 1}
      </Table.TableCell>
      <Table.TableCell className={cn("w-[300px]", commonCellClassName)}>
        <FormInput
          name={`employeeWorkingHours.${index}.employeeId`}
          form={form}
          render={(field) => (
            <FormSelectSingle
              field={field}
              options={
                Array.isArray(employees) && employees?.length
                  ? employees?.map((employee: IEmployee) => ({
                      label: employee?.user?.firstName || "",
                      value: employee?.id,
                    }))
                  : []
              }
              isLoading={isLoading}
              placeholder='Select Employee'
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <FormInput
          name={`employeeWorkingHours.${index}.startTime`}
          form={form}
          render={(field) => (
            <TimePicker12
              disabled={!day}
              date={startTime}
              setDate={(date) => {
                field.onChange(handleTime(date!));
                setStartTime(date);
              }}
              AMPM={startPeriod}
              setAMPM={setStartPeriod}
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <FormInput
          name={`employeeWorkingHours.${index}.endTime`}
          form={form}
          render={(field) => (
            <TimePicker12
              disabled={!day}
              date={endTime}
              setDate={(date) => {
                field.onChange(handleTime(date!));
                setEndTime(date);
              }}
              AMPM={endPeriod}
              setAMPM={setEndPeriod}
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[130px]")}>
        <FormInput
          name={`employeeWorkingHours.${index}.workBreakDown.${0}.time`}
          form={form}
          render={() => {
            const displayValue = secToDateFormat(
              form.getValues(
                `employeeWorkingHours.${index}.workBreakDown.${0}.time`,
              ),
            );

            return (
              <Input
                type='text'
                className='max-w-fit'
                disabled
                value={displayValue}
              />
            );
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[130px]")}>
        <FormInput
          name={`employeeWorkingHours.${index}.workBreakDown.${1}.time`}
          form={form}
          render={() => {
            const displayValue = secToDateFormat(
              form.getValues(
                `employeeWorkingHours.${index}.workBreakDown.${1}.time`,
              ),
            );

            return (
              <Input
                type='text'
                className='max-w-fit'
                disabled
                value={displayValue}
              />
            );
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[130px]")}>
        <FormInput
          name={`employeeWorkingHours.${index}.workBreakDown.${2}.time`}
          form={form}
          render={() => {
            const displayValue = secToDateFormat(
              form.getValues(
                `employeeWorkingHours.${index}.workBreakDown.${2}.time`,
              ),
            );

            return (
              <Input
                type='text'
                className='max-w-fit'
                disabled
                value={displayValue}
              />
            );
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[120px]")}>
        <FormInput
          name={`employeeWorkingHours.${index}.break`}
          form={form}
          render={(fields) => (
            <Input
              placeholder={`0`}
              onKeyDown={(e) =>
                InputKeyLimiter({
                  event: e,
                  maxValue:
                    form.watch(`employeeWorkingHours.${index}.totalHours`) ||
                    24,
                })
              }
              {...fields}
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <p className='text-center text-aldgray'>
          <NumberFlow
            value={form.watch(`employeeWorkingHours.${index}.totalHours`) || 0}
          />
        </p>
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[600px] ")}>
        <FormInput
          name={`employeeWorkingHours.${index}.files`}
          form={form}
          render={(field, _, formState) => (
            <FileUploaderComponent
              field={field}
              formState={formState}
              maxFilePreview={2}
              isMultiple
              {...field}
              isForFileUpload={true}
              acceptFileTypes={allowedImageTypes}
            />
          )}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[80px] ")}>
        <div className='flex justify-center'>
          <MapPin
            color='green'
            className='cursor-pointer '
            onClick={() => setOpenLocationMap(true)}
          />
          <LocationMap
            projectAddress={projectAddress}
            open={openLocationMap}
            propMarkers={
              form.watch(
                `employeeWorkingHours.${index}.location`,
              ) as MarkerData[]
            }
            onClose={() => setOpenLocationMap(false)}
          />
        </div>
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName, "w-[280px]")}>
        <FormInput
          name={`employeeWorkingHours.${index}.note`}
          form={form}
          input={{
            placeholder: "Notes",
          }}
        />
      </Table.TableCell>
      <Table.TableCell className={cn(commonCellClassName)}>
        <Trash2
          size={20}
          color='red'
          onClick={() => {
            handleWhDelete(
              index,
              form.getValues(`employeeWorkingHours.${index}.id`),
            );
          }}
        />
      </Table.TableCell>
    </Table.TableRow>
  );
};

const EmployeeWH = ({
  projectAddress,
  holiday,
}: {
  projectAddress?: MapAddress;
  holiday: Holiday | undefined;
}): React.JSX.Element => {
  const { data: shifts, isLoading: isEmployeeLoading } =
    useGetShiftsQuery("EMPLOYEE");

  const form = useFormContext<TDailyReport>();
  const { control } = form;
  const [defaultBreakDown, setDefaultBreakDown] = useState<
    {
      shiftId: string;
      time: number;
    }[]
  >();

  const {
    fields: EmployeeField,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "employeeWorkingHours",
    keyName: "arrayId",
  });

  const { data: employees, isLoading } = useGetUsersByWSQuery({
    userType: "employee",
    status: "ACTIVE",
  });

  const { theme: skin } = useTheme();

  const addEmployee = (): void => {
    append({
      employeeId: "",
      startTime: 0,
      endTime: 0,
      break: 0,
      workBreakDown: defaultBreakDown || [],
      location: [],
      totalHours: 0,
    });
  };

  // set the default workbreakdown with id and time according to the shifts in settings
  useEffect(() => {
    if (!isEmployeeLoading && shifts) {
      const reformat = sortShifts(shifts);
      const [sortedShifts] = reformat;
      const breakDowns = sortedShifts.map((shift: TimeShift) => ({
        shiftId: shift.id,
        time: 0,
      }));
      setDefaultBreakDown(breakDowns);
    }
  }, [isEmployeeLoading, shifts, form]);

  return (
    <div className='my-8'>
      <h2 className='mb-4 text-lg font-medium'>Employee Working Hours</h2>
      <div className='overflow-auto border rounded-md '>
        <Table.Table className=''>
          <Table.TableHeader>
            <Table.TableRow
              className={cn(
                skin !== "dark" && "bg-[#E2EBF5] hover:bg-secondary ",
              )}
            >
              <Table.TableHead className='px-2'>S.N</Table.TableHead>
              <Table.TableHead className='min-w-[250px]'>
                EMPLOYEE NAME
              </Table.TableHead>
              <Table.TableHead>START TIME</Table.TableHead>
              <Table.TableHead>END TIME</Table.TableHead>
              <Table.TableHead className='min-w-[110px]'>
                NORMAL{" "}
              </Table.TableHead>
              <Table.TableHead className='min-w-[110px]'>
                OVERTIME{" "}
              </Table.TableHead>
              <Table.TableHead className='min-w-[110px]'>
                WEEKEND{" "}
              </Table.TableHead>
              <Table.TableHead className='min-w-[80px]'>BREAK</Table.TableHead>
              <Table.TableHead>TOTAL </Table.TableHead>
              <Table.TableHead>IMAGE </Table.TableHead>
              <Table.TableHead>LOCATION</Table.TableHead>
              <Table.TableHead className='min-w-[200px]'>
                NOTES{" "}
              </Table.TableHead>
              <Table.TableHead> </Table.TableHead>
            </Table.TableRow>
          </Table.TableHeader>
          <Table.TableBody>
            {EmployeeField.map((field, index) => (
              <SingleEmployeeWH
                projectAddress={projectAddress}
                key={field.arrayId}
                index={index}
                field={field}
                remove={remove}
                employees={employees?.data as IEmployee[]}
                isLoading={isLoading}
                form={form}
                shifts={shifts || []}
                holiday={holiday}
                // breakList={(breakList?.data as IBreak[]) || []}
              />
            ))}
            <Table.TableRow className='!bg-background dark:bg-primary'>
              <Table.TableCell colSpan={11} className='text-left'>
                <Button
                  type='button'
                  onClick={() => addEmployee()}
                  variant={"light"}
                  className={cn("ml-2 my-2")}
                >
                  + Add New Row
                </Button>
              </Table.TableCell>
            </Table.TableRow>
          </Table.TableBody>
        </Table.Table>
      </div>
    </div>
  );
};

export default EmployeeWH;
